import React, { useState, useEffect, useCallback, useMemo } from "react";
import { FieldChips, type ChipItem, type ChipOption, type FieldChipsState } from "@/components/ui/FieldChips";
import { supabase } from "@/lib/supabase-client";

type InheritedMarket = { id: string; name: string; fips_code?: string; label?: string };

interface MarketsInputProps {
  /** Array of market IDs currently assigned to the entity */
  value: string[];
  /** Single inherited market from location — shown as readonly chip */
  inheritedMarket?: InheritedMarket | null;
  /** Default label for the single inheritedMarket badge */
  inheritedLabel?: string;
  /** Additional inherited markets (e.g. from project) — each with its own label */
  inheritedMarkets?: InheritedMarket[];
  state?: FieldChipsState;
  label?: string;
  error?: string;
  onChange?: (marketIds: string[]) => void;
}

export function MarketsInput({
  value,
  inheritedMarket = null,
  inheritedLabel = "From Location",
  inheritedMarkets = [],
  state = "editing",
  label = "Markets",
  error,
  onChange,
}: MarketsInputProps) {
  const [options, setOptions] = useState<ChipOption[]>([]);
  const [loadedMarkets, setLoadedMarkets] = useState<{ id: string; name: string; fips_code?: string }[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch all market options on mount
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const { data, error: fetchError } = await supabase
          .schema("lists")
          .from("markets")
          .select("id, name, fips_code")
          .order("name", { ascending: true });
        if (fetchError) throw fetchError;
        if (cancelled) return;
        setOptions(
          (data ?? []).map((m) => ({
            id: m.id,
            name: m.name,
            description: m.fips_code ? `FIPS: ${m.fips_code}` : undefined,
          })),
        );
      } catch (err) {
        console.error("Failed to load markets:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch details for currently-assigned market IDs
  useEffect(() => {
    if (!value || value.length === 0) {
      setLoadedMarkets([]);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const { data, error: fetchError } = await supabase
          .schema("lists")
          .from("markets")
          .select("id, name, fips_code")
          .in("id", value);
        if (fetchError) throw fetchError;
        if (!cancelled) setLoadedMarkets(data ?? []);
      } catch (err) {
        console.error("Failed to load assigned markets:", err);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [value]);

  // Build chip items: inherited markets + inherited location market + assigned
  const chipItems = useMemo<ChipItem[]>(() => {
    const chips: ChipItem[] = [];
    const inheritedIds = new Set<string>();

    // Additional inherited markets (e.g. from project)
    for (const im of inheritedMarkets) {
      if (inheritedIds.has(im.id)) continue;
      inheritedIds.add(im.id);
      chips.push({
        id: `inherited-${im.id}`,
        name: im.name,
        sourceLabel: im.label || "Inherited",
        readonly: true,
      });
    }

    // Single inherited market from location
    if (inheritedMarket && !inheritedIds.has(inheritedMarket.id)) {
      inheritedIds.add(inheritedMarket.id);
      chips.push({
        id: `inherited-${inheritedMarket.id}`,
        name: inheritedMarket.name,
        sourceLabel: inheritedMarket.label || inheritedLabel,
        readonly: true,
      });
    }

    for (const m of loadedMarkets) {
      // Skip if already shown as inherited
      if (inheritedIds.has(m.id)) continue;
      chips.push({
        id: m.id,
        name: m.name,
        subtitle: m.fips_code ? `FIPS: ${m.fips_code}` : undefined,
        readonly: false,
      });
    }
    return chips;
  }, [loadedMarkets, inheritedMarket, inheritedLabel, inheritedMarkets]);

  const handleAdd = useCallback(
    (marketId: string) => {
      if (value.includes(marketId)) return;
      onChange?.([...value, marketId]);
    },
    [value, onChange],
  );

  const handleRemove = useCallback(
    (chipId: string) => {
      onChange?.(value.filter((id) => id !== chipId));
    },
    [value, onChange],
  );

  const resolvedState: FieldChipsState = loading ? "loading" : state;

  return (
    <FieldChips
      label={label}
      icon="earth"
      accentTone="sky"
      items={chipItems}
      options={options}
      state={resolvedState}
      placeholder="Search markets…"
      error={error}
      onAdd={handleAdd}
      onRemove={handleRemove}
    />
  );
}
